import { computed, effectScope, ref, watch, type EffectScope, type Ref } from "vue";

import { useAppConfig } from "@core/composables/data/useAppConfig";
import { usePermissions } from "@core/composables/data/usePermissions";
import { useRouterContext } from "@core/composables/state/useRouteContext";
import { quickNavService, type QuickNavResult } from "@core/services/quickNavService";

import { useTranslate } from "@common/composables/useTranslate";
import { debounce } from "@common/utils/debounce";

import { queueDashboardScrollReset } from "@/router";

const RECENT_SEARCHES_KEY = "youpark-quicknav-recent";
const MAX_RECENT = 5;
const FAVORITES_KEY = "youpark-quicknav-favorites";
const MAX_FAVORITES = 5;
const DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

type SelectedAction = "organization" | "remit" | "youpark";
type SearchMode = "organizations" | "commands";

export interface MenuItem {
  id: string;
  translationKey: string;
  category: "user" | "admin";
  permission?: string;
  requiresAuth?: boolean;
}

const MENU_ITEMS: MenuItem[] = [
  { id: "home", translationKey: "nav.home", category: "user", requiresAuth: true },
  { id: "profile", translationKey: "nav.profile", category: "user", requiresAuth: true },
  { id: "vehicles", translationKey: "nav.vehicles", category: "user", requiresAuth: true },
  { id: "payment-sources", translationKey: "nav.paymentSource", category: "user", requiresAuth: true },
  { id: "subscriptions", translationKey: "nav.subscriptions", category: "user", requiresAuth: true },
  { id: "bookings", translationKey: "nav.bookings", category: "user", requiresAuth: true },
  { id: "coupons", translationKey: "nav.coupons", category: "user", requiresAuth: true },
  { id: "receipts", translationKey: "nav.receipts", category: "user", requiresAuth: true },
  { id: "payment", translationKey: "nav.paymentQuick", category: "user" },
  { id: "map-parking", translationKey: "nav.parkering", category: "user" },
  { id: "map-toll", translationKey: "nav.bomvei", category: "user" },
  { id: "customer-service", translationKey: "nav.customerService", category: "user" },
  { id: "faq", translationKey: "nav.faq", category: "user" },
  { id: "statistics", translationKey: "nav.statistics", category: "admin", permission: "statistics" },
  { id: "telemetry", translationKey: "nav.telemetry", category: "admin", permission: "telemetry" },
  { id: "applications", translationKey: "nav.applications", category: "admin", permission: "applications" },
  { id: "users", translationKey: "nav.users", category: "admin", permission: "users" },
  { id: "organizations", translationKey: "nav.organizations", category: "admin", permission: "organizations" },
  { id: "audits", translationKey: "nav.audits", category: "admin", permission: "audits" },
  { id: "exports", translationKey: "nav.exports", category: "admin", permission: "audits" },
  { id: "vehicle-registry", translationKey: "nav.vehicleRegistry", category: "admin", permission: "vehicleRegistry" },
  { id: "manual-approval", translationKey: "nav.manualApproval", category: "admin", permission: "manualApproval" },
  { id: "cameras-overview", translationKey: "nav.camerasOverview", category: "admin", permission: "camerasOverview" },
  { id: "traffic-control", translationKey: "nav.trafficControl", category: "admin", permission: "trafficControl" }
];

const loadRecentSearches = (): QuickNavResult[] => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!stored) {
      return [];
    }
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? (parsed as QuickNavResult[]).slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
};

const saveRecentSearches = (items: QuickNavResult[]) => {
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(items.slice(0, MAX_RECENT)));
};

const loadFavorites = (): QuickNavResult[] => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    if (!stored) {
      return [];
    }
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? (parsed as QuickNavResult[]).slice(0, MAX_FAVORITES) : [];
  } catch {
    return [];
  }
};

const saveFavorites = (items: QuickNavResult[]) => {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(items.slice(0, MAX_FAVORITES)));
};

interface QuickNavState {
  isOpen: Ref<boolean>;
  searchQuery: Ref<string>;
  results: Ref<QuickNavResult[]>;
  isLoading: Ref<boolean>;
  isNavigating: Ref<boolean>;
  selectedIndex: Ref<number>;
  selectedAction: Ref<SelectedAction>;
  recentSearches: Ref<QuickNavResult[]>;
  favorites: Ref<QuickNavResult[]>;
  searchMode: Ref<SearchMode>;
}

const createQuickNavState = (): QuickNavState => ({
  isOpen: ref(false),
  searchQuery: ref(""),
  results: ref<QuickNavResult[]>([]),
  isLoading: ref(false),
  isNavigating: ref(false),
  selectedIndex: ref(0),
  selectedAction: ref<SelectedAction>("youpark"),
  recentSearches: ref<QuickNavResult[]>(loadRecentSearches()),
  favorites: ref<QuickNavResult[]>(loadFavorites()),
  searchMode: ref<SearchMode>("organizations")
});

let sharedQuickNavState: QuickNavState | null = null;
let sharedQuickNavController: QuickNavController | null = null;
let sharedQuickNavScope: EffectScope | null = null;
let sharedDebouncedSearch: ((query: string) => void) | null = null;

const getQuickNavState = () => {
  sharedQuickNavState ??= createQuickNavState();
  return sharedQuickNavState;
};

const saveToRecent = (result: QuickNavResult) => {
  const { recentSearches } = getQuickNavState();
  const existing = recentSearches.value.filter(r => r.organizationId !== result.organizationId);
  const updated = [result, ...existing].slice(0, MAX_RECENT);
  recentSearches.value = updated;
  saveRecentSearches(updated);
};

export interface MenuItemWithLabel extends MenuItem {
  label: string;
}

interface QuickNavController {
  isOpen: Ref<boolean>;
  searchQuery: Ref<string>;
  results: Ref<QuickNavResult[]>;
  isLoading: Ref<boolean>;
  isNavigating: Ref<boolean>;
  selectedIndex: Ref<number>;
  selectedAction: Ref<SelectedAction>;
  recentSearches: Ref<QuickNavResult[]>;
  searchMode: Ref<SearchMode>;
  menuResults: Ref<MenuItemWithLabel[]>;
  canAccessOrganization: Ref<boolean>;
  canAccessRemit: Ref<boolean>;
  canAccessYoupark: Ref<boolean>;
  canShow: Ref<boolean>;
  open: () => void;
  close: () => void;
  handleKeyDown: (e: KeyboardEvent) => void;
  handleGlobalKeyDown: (e: KeyboardEvent) => void;
  navigateToOrganization: (result: QuickNavResult) => Promise<void>;
  navigateToYoupark: (result: QuickNavResult) => Promise<void>;
  navigateToRemit: (result: QuickNavResult) => Promise<void>;
  navigateToMenuItem: (item: MenuItemWithLabel) => Promise<void>;
  performSearch: (query: string) => void;
  removeFromRecent: (result: QuickNavResult) => void;
  clearRecentSearches: () => void;
  favorites: Ref<QuickNavResult[]>;
  filteredRecentSearches: Ref<QuickNavResult[]>;
  isFavorite: (result: QuickNavResult) => boolean;
  toggleFavorite: (result: QuickNavResult) => void;
}

export function useQuickNav(): QuickNavController {
  if (sharedQuickNavController) {
    return sharedQuickNavController;
  }

  const state = getQuickNavState();
  const router = useRouterContext();
  const translate = useTranslate();
  const { config } = useAppConfig();
  const { canAccessYouParkCustomer, permissions } = usePermissions();
  const {
    isOpen,
    searchQuery,
    results,
    isLoading,
    isNavigating,
    selectedIndex,
    selectedAction,
    recentSearches,
    favorites
  } = state;
  const { searchMode } = state;

  const canAccessOrganization = computed(() => permissions.value.organizations);
  const canAccessRemit = computed(() => permissions.value.remits);
  const canAccessYoupark = computed(() => canAccessYouParkCustomer.value);
  const canShow = computed(() => canAccessYoupark.value || canAccessOrganization.value || canAccessRemit.value);

  const filteredRecentSearches = computed(() => {
    const favoriteIds = new Set(favorites.value.map(f => f.organizationId));
    return recentSearches.value.filter(r => !favoriteIds.has(r.organizationId));
  });

  const isFavorite = (result: QuickNavResult) => {
    return favorites.value.some(f => f.organizationId === result.organizationId);
  };

  const toggleFavorite = (result: QuickNavResult) => {
    if (isFavorite(result)) {
      const updated = favorites.value.filter(f => f.organizationId !== result.organizationId);
      favorites.value = updated;
      saveFavorites(updated);
    } else if (favorites.value.length < MAX_FAVORITES) {
      const updated = [...favorites.value, result];
      favorites.value = updated;
      saveFavorites(updated);
    }
  };

  const availableMenuItems = computed<MenuItemWithLabel[]>(() => {
    return MENU_ITEMS.filter(item => {
      if (item.requiresAuth && !config.isAuthenticated) {
        return false;
      }
      if (item.permission) {
        const perm = item.permission as keyof typeof permissions.value;
        if (!permissions.value[perm]) {
          return false;
        }
      }
      return true;
    }).map(item => ({
      ...item,
      label: translate(item.translationKey)
    }));
  });

  const menuResults = computed<MenuItemWithLabel[]>(() => {
    if (searchMode.value !== "commands") {
      return [];
    }

    const query = searchQuery.value.slice(1).trim().toLowerCase();
    if (!query) {
      return availableMenuItems.value;
    }

    return availableMenuItems.value.filter(item => item.label.toLowerCase().includes(query));
  });

  const executeSearch = async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      results.value = [];
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    try {
      results.value = await quickNavService.searchAll(trimmed);
    } catch {
      results.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  sharedDebouncedSearch ??= debounce((query: string) => {
    void executeSearch(query);
  }, DEBOUNCE_MS);

  const performSearch = (query: string) => {
    if (query.trim().length < MIN_SEARCH_LENGTH) {
      results.value = [];
      isLoading.value = false;
      return;
    }
    isLoading.value = true;
    sharedDebouncedSearch?.(query);
  };

  const open = () => {
    isOpen.value = true;
    searchQuery.value = "";
    results.value = [];
    selectedIndex.value = 0;
    selectedAction.value = canAccessYoupark.value ? "youpark" : canAccessRemit.value ? "remit" : "organization";
    searchMode.value = "organizations";
    isLoading.value = false;
  };

  const close = () => {
    isOpen.value = false;
  };

  const navigateToOrganization = async (result: QuickNavResult) => {
    saveToRecent(result);
    isNavigating.value = true;

    try {
      await router.push({
        name: "organizations-details",
        params: { id: result.organizationId }
      });
    } finally {
      isNavigating.value = false;
      close();
    }
  };

  const navigateToYoupark = async (result: QuickNavResult) => {
    saveToRecent(result);
    isNavigating.value = true;

    try {
      await router.push({
        name: "youpark",
        params: { organizationId: result.organizationId }
      });
    } finally {
      isNavigating.value = false;
      close();
    }
  };

  const navigateToRemit = async (result: QuickNavResult) => {
    saveToRecent(result);
    isNavigating.value = true;

    try {
      await router.push({
        name: "remit",
        params: { organizationId: result.organizationId }
      });
      queueDashboardScrollReset();
    } finally {
      isNavigating.value = false;
      close();
    }
  };

  const navigateToMenuItem = async (item: MenuItemWithLabel) => {
    isNavigating.value = true;

    try {
      await router.push({ name: item.id });
    } finally {
      isNavigating.value = false;
      close();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen.value || isNavigating.value) {
      return;
    }

    if (searchMode.value === "commands") {
      const items = menuResults.value;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          selectedIndex.value = Math.min(selectedIndex.value + 1, items.length - 1);
          break;
        case "ArrowUp":
          e.preventDefault();
          selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex.value < items.length) {
            void navigateToMenuItem(items[selectedIndex.value]);
          }
          break;
        case "Escape":
          e.preventDefault();
          close();
          break;
      }
      return;
    }

    const displayItems =
      searchQuery.value.trim().length >= MIN_SEARCH_LENGTH
        ? results.value
        : [...favorites.value, ...filteredRecentSearches.value];

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        selectedIndex.value = Math.min(selectedIndex.value + 1, displayItems.length - 1);
        selectedAction.value = canAccessYoupark.value ? "youpark" : canAccessRemit.value ? "remit" : "organization";
        break;
      case "ArrowUp":
        e.preventDefault();
        selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
        selectedAction.value = canAccessYoupark.value ? "youpark" : canAccessRemit.value ? "remit" : "organization";
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (selectedAction.value === "youpark") {
          if (canAccessRemit.value) {
            selectedAction.value = "remit";
          } else if (canAccessOrganization.value) {
            selectedAction.value = "organization";
          }
        } else if (selectedAction.value === "remit") {
          if (canAccessOrganization.value) {
            selectedAction.value = "organization";
          }
        }
        break;
      case "ArrowRight":
        e.preventDefault();
        if (selectedAction.value === "organization") {
          if (canAccessRemit.value) {
            selectedAction.value = "remit";
          } else if (canAccessYoupark.value) {
            selectedAction.value = "youpark";
          }
        } else if (selectedAction.value === "remit") {
          if (canAccessYoupark.value) {
            selectedAction.value = "youpark";
          }
        }
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex.value < displayItems.length) {
          const result = displayItems[selectedIndex.value];
          if (selectedAction.value === "organization" && canAccessOrganization.value) {
            void navigateToOrganization(result);
          } else if (selectedAction.value === "remit" && canAccessRemit.value) {
            void navigateToRemit(result);
          } else if (selectedAction.value === "youpark" && canAccessYoupark.value) {
            void navigateToYoupark(result);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
    }
  };

  const handleGlobalKeyDown = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
      e.preventDefault();
      if (canShow.value) {
        if (isOpen.value) {
          close();
        } else {
          open();
        }
      }
    }
  };

  const removeFromRecent = (result: QuickNavResult) => {
    const updated = recentSearches.value.filter(r => r.organizationId !== result.organizationId);
    recentSearches.value = updated;
    saveRecentSearches(updated);
    const totalItems = favorites.value.length + filteredRecentSearches.value.length;
    if (selectedIndex.value >= totalItems) {
      selectedIndex.value = Math.max(0, totalItems - 1);
    }
  };

  const clearRecentSearches = () => {
    recentSearches.value = [];
    saveRecentSearches([]);
    selectedIndex.value = 0;
  };

  if (!sharedQuickNavScope) {
    sharedQuickNavScope = effectScope(true);
    sharedQuickNavScope.run(() => {
      watch(searchQuery, value => {
        selectedIndex.value = 0;

        if (value.startsWith("/")) {
          searchMode.value = "commands";
          isLoading.value = false;
          results.value = [];
        } else {
          searchMode.value = "organizations";
          selectedAction.value = "youpark";
          performSearch(value);
        }
      });
    });
  }

  sharedQuickNavController = {
    isOpen,
    searchQuery,
    results,
    isLoading,
    isNavigating,
    selectedIndex,
    selectedAction,
    recentSearches,
    searchMode,
    menuResults,
    canAccessOrganization,
    canAccessRemit,
    canAccessYoupark,
    canShow,
    open,
    close,
    handleKeyDown,
    handleGlobalKeyDown,
    navigateToOrganization,
    navigateToYoupark,
    navigateToRemit,
    navigateToMenuItem,
    performSearch,
    removeFromRecent,
    clearRecentSearches,
    favorites,
    filteredRecentSearches,
    isFavorite,
    toggleFavorite
  };

  return sharedQuickNavController;
}
